import type SysColumn from "app/class/sys-column";
import type SysTable from "app/class/sys-table";
import type { TFunction } from "i18next";
import { getOperatorConfig } from "modules/record/record-producer/components/record-producer-fields/condition-field/utils/operators.config";
import { SYSTEM_DYNAMIC_OPTIONS } from "modules/record/record-producer/components/record-producer-fields/condition-field/systemDynamicOptions";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Mirrors ConditionBuilderField: a REFERENCE dependency stores a table id;
// a textual dependency stores the table name.
export function resolveConditionColumns(
    conditionColumn: SysColumn,
    row: Record<string, any>,
    tableColumns: SysColumn[],
    allColumns: SysColumn[],
    tables: SysTable[]
): SysColumn[] {
    const dependency = tableColumns.find((candidate) => candidate.id === conditionColumn.depends_on_column_id);
    if (!dependency) return [];
    const raw = row?.[dependency.name];
    const value = typeof raw === "object" && raw !== null ? raw.id : raw;
    if (typeof value !== "string" || !value) return [];
    const target = dependency.column_type === "REFERENCE"
        ? tables.find((table) => table.id === value)
        : tables.find((table) => table.name === value);
    return target ? allColumns.filter((column) => column.sys_table_id === target.id) : [];
}

const dynamicLabels = new Map(SYSTEM_DYNAMIC_OPTIONS.map((option) => [option.value, option.label]));

function formatValue(raw: unknown, column: SysColumn | undefined, t: TFunction, choices: Record<string, Map<string, string>>, tables: SysTable[], allColumns: SysColumn[]): string {
    if (Array.isArray(raw)) return raw.map((item) => formatValue(item, column, t, choices, tables, allColumns)).filter(Boolean).join(", ");
    if (raw == null) return "";
    if (typeof raw === "object") {
        const candidate = raw as Record<string, unknown>;
        if (column?.column_type === "REFERENCE") {
            const display = candidate.display ?? candidate.label ?? candidate.name;
            if (display != null) return formatValue(display, undefined, t, choices, tables, allColumns);
            return formatValue(candidate.id, column, t, choices, tables, allColumns);
        }
        return formatValue(candidate.display ?? candidate.label ?? candidate.name, column, t, choices, tables, allColumns);
    }
    const text = String(raw);
    const dynamicLabel = dynamicLabels.get(text);
    if (dynamicLabel) return String(t(dynamicLabel));
    // Unknown dynamic expressions and reference ids have no safe display value.
    if (/\{\{|\$\{/.test(text)) return "";
    if (column?.column_type === "REFERENCE") {
        if (column.referenced_table_name === "sys_tables") {
            const table = tables.find((candidate) => candidate.id === text);
            if (table) return translateMetadataLabel(t, table);
        }
        if (column.referenced_table_name === "sys_columns") {
            const referencedColumn = allColumns.find((candidate) => candidate.id === text);
            if (referencedColumn) return translateMetadataLabel(t, referencedColumn);
        }
        return "";
    }
    if (UUID.test(text)) return "";
    const choice = column ? choices[column.id]?.get(text) : undefined;
    const display = choice || (/^[A-Z][A-Z_]*$/.test(text)
        ? text.toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : text);
    return String(t(display));
}

export function formatConditionSummary(
    raw: unknown,
    columns: SysColumn[],
    t: TFunction,
    choices: Record<string, Map<string, string>> = {},
    tables: SysTable[] = [],
    allColumns: SysColumn[] = []
): string {
    try {
        const root = typeof raw === "string" ? JSON.parse(raw) : raw;
        const format = (node: any): string => {
            if (!node || typeof node !== "object") return "";
            if (node.type === "logical") {
                const parts = Array.isArray(node.conditions) ? node.conditions.map(format).filter(Boolean) : [];
                return parts.length > 1 ? parts.join(` ${t(node.operator === "or" ? "OR" : "AND")} `) : parts[0] || "";
            }
            if (node.type === "exists") return node.table && !UUID.test(node.table) ? `${t("EXISTS")} ${t(node.table)}` : String(t("EXISTS"));
            if (node.type !== "condition") return "";
            const column = columns.find((candidate) => candidate.name === node.field);
            const field = column ? translateMetadataLabel(t, column) : (!UUID.test(String(node.field)) ? String(node.field || "") : "");
            if (!field) return "";
            const operator = getOperatorConfig(column?.column_type || "TEXT", node.operator)?.label || node.operator;
            const translatedOperator = operator && !UUID.test(String(operator)) ? t(String(operator)) : "";
            const value = formatValue(node.value, column, t, choices, tables, allColumns);
            return [field, translatedOperator, value].filter(Boolean).join(" ");
        };
        return format(root) || "—";
    } catch {
        return "—";
    }
}
